import { busesAPI } from "components/Apis/Buses";
import { ordersApi } from "components/Apis/orders";
import { useEffect, useMemo, useRef, useState } from "react";
import { Collapse } from "react-bootstrap";
import Block from "./components/block";
import "./styles.scss";
import { generateColorSet } from "./utils";

/**
 * Props:
 *   busId = number               // bus to load packing plan for
 *   trailer = { widthIn?: number, lengthIn?: number, heightIn?: number }
 *     // optional overrides; defaults: 100 x 636 x 110
 */
export default function BusPlan({ busId, orderId }) {
  const containerRef = useRef(null);
  const legendRef = useRef(null);

  const [packingPlan, setPackingPlan] = useState(null);
  const [trailer, setTrailer] = useState({});

  const [showOverflowBlocks, setShowOverflowBlocks] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (busId) {
        try {
          const response = await busesAPI.getPackingPlan(busId);
          setTrailer(response.trailer || {});
          setPackingPlan(response);
        } catch (error) {
          console.error("Error fetching packing plan:", error);
        }
      }
      else if (orderId) {
        try {
          const response = await ordersApi.getPackingPlan(orderId);
          setTrailer(response.data.trailer || {});
          setPackingPlan(response.data);
        } catch (error) {
          console.error("Error fetching packing plan by order:", error);
        }
      }
      else {
        console.warn("No busId or orderId provided for TrailerPackingPlan");
      }
    }
    fetchData();
  }, [busId, orderId]);
  const TW = Number(trailer?.widthIn ?? 100);   // across (in)
  const TL = Number(trailer?.lengthIn ?? 636);  // down (in)
  const TH = Number(trailer?.heightIn ?? 110);  // vertical (in)

  const [containerWidth, setContainerWidth] = useState(800);
  const [viewportAvailPx, setViewportAvailPx] = useState(800);
  const [legendHeight, setLegendHeight] = useState(0);

  const updateMeasures = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setContainerWidth(containerRef.current.clientWidth || 800);
    setViewportAvailPx(Math.max(200, window.innerHeight - rect.top - 10));
    setLegendHeight(legendRef.current?.offsetHeight || 0);
  };

  useEffect(() => {
    updateMeasures();
    window.addEventListener("resize", updateMeasures);
    return () => window.removeEventListener("resize", updateMeasures);
  }, []);

  const norm = useMemo(() => normalizeData(packingPlan, TW, TL, TH), [packingPlan, TW, TL, TH]);
  const overflowBlocks = useMemo(() => normalizeOverflow(packingPlan), [packingPlan]);

  const orderColors = useMemo(() => {
    const uniqueIds = packingPlan?.orders?.map((o) => o.order_no) || [];
    return generateColorSet(uniqueIds, { format: "hex" });
  }, [packingPlan]);

  // layout: show full trailer length for stable grid
  const availableHeightPx = Math.max(60, viewportAvailPx - legendHeight - 16);
  const AXIS_LEFT_PX = 46;
  const AXIS_BORDER_PX = 1;
  const availWidthPx = Math.max(120, containerWidth - AXIS_LEFT_PX - AXIS_BORDER_PX);

  // Size the trailer canvas from actual trailer proportions (TL x TW),
  // constrained by current viewport and container width.
  const trailerAspect = TL / Math.max(TW, 1);
  const canvasHeightFromWidthPx = availWidthPx / trailerAspect;
  const canvasWidthFromHeightPx = availableHeightPx * trailerAspect;
  const canvasHeightPx = Math.min(availableHeightPx, canvasHeightFromWidthPx);
  const canvasWidthPx = Math.min(availWidthPx, canvasWidthFromHeightPx);
  const pxPerIn = Math.min(canvasWidthPx / TL, canvasHeightPx / TW);
  const containerHeight = Math.max(
    260,
    Math.min(viewportAvailPx, canvasHeightPx + legendHeight + 84)
  );

  const ticksX = useMemo(() => buildTicks(TL), [TL]);
  const ticksY = useMemo(() => buildTicks(TW), [TW]);

  return (
    <div
      ref={containerRef}
      className="tp-container"
      style={{ "--tp-height": `${containerHeight}px` }}
    >
      <div
        className="tp-stage"
        style={{
          "--tp-axis-left-w": `${AXIS_LEFT_PX}px`,
          "--tp-axis-border-w": `${AXIS_BORDER_PX}px`,
          "--tp-canvas-w": `${canvasWidthPx}px`,
          "--tp-canvas-h": `${canvasHeightPx}px`,
          "--tp-in": `${pxPerIn}px`,
          "--tp-axis-gap": "4px",
        }}
      >
        {/* Top axis (feet along length) */}
        <div className="tp-axis tp-axis--top">
          {ticksX.slice(1, -1).map((inch) => (
            <div key={inch} className="tp-tick" style={{ "--tp-x": `${inch * pxPerIn}px` }}>
              <span className="tp-tick__label">
                <span className="tp-tick__feet">{feetLabel(inch)}</span>
                <span className="tp-tick__in">{inchLabel(inch)}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="tp-stage-row">
          {/* Left axis (feet across width) */}
          <div className="tp-axis tp-axis--left">
            {ticksY.slice(1, -1).map((inch) => (
              <div key={inch} className="tp-vtick" style={{ "--tp-y": `${inch * pxPerIn}px` }}>
                <span className="tp-vtick__label">
                  <span className="tp-vtick__feet">{feetLabel(inch)}</span>
                  <span className="tp-vtick__in">{inchLabel(inch)}</span>
                </span>
              </div>
            ))}
          </div>

          {/* Canvas */}
          <div className="tp-canvas">

            {/* Blocks */}
            {norm.map((b, i) => (
              <Block
                key={i}
                block={b}
                pxPerIn={pxPerIn}
                orderColors={orderColors}
                tooltipFn={tooltip}        // you already have this helper
              />
            ))}
          </div>
        </div>
      </div>

      <div className="tp-spacer" style={{ height: "8px" }} />

      {/* Overflow blocks (below canvas, above legend) */}
      <div className="tp-overflow">
        {overflowBlocks.length > 0 ? (
          <>
            <div className="tp-overflow__toggle">
              <span className="tp-overflow__line" aria-hidden="true"></span>
              <button
                type="button"
                className="tp-overflow__pill"
                onClick={() => setShowOverflowBlocks(!showOverflowBlocks)}
              >
                <span className={`tp-overflow__chevron ${showOverflowBlocks ? "is-open" : ""}`}>
                  <i className="fa fa-chevron-down"></i>
                </span>
                <span className="tp-overflow__label">Overflow blocks</span>
                <span className="tp-overflow__count">({overflowBlocks.length})</span>
              </button>
              <span className="tp-overflow__line" aria-hidden="true"></span>
            </div>
            <Collapse in={showOverflowBlocks}>
              <div className="tp-overflow__grid">
                {overflowBlocks.map((b, i) => (
                  <div key={i} className="tp-overflow__item">
                    <Block
                      block={b}
                      pxPerIn={pxPerIn}
                      orderColors={orderColors}
                      tooltipFn={tooltip}
                      overflow={true}
                    />
                  </div>
                ))}
              </div>
            </Collapse>
          </>
        ) : (
          <div className="tp-overflow__empty">No overflow blocks.</div>
        )}
      </div>

      {/* Footer / Legend (optional) */}
      <div ref={legendRef} className="tp-legend">
        <div className="tp-legend__wrap">
          {
            packingPlan?.orders?.map((o) => (
              <div key={o.order_no} className="tp-legend__item">
                <span
                  className="tp-legend__swatch"
                  style={{ "--tp-swatch": orderColors[o.order_no] || "#000000" }}
                />
                <span className="tp-legend__label">
                  <strong>{o.order_no}</strong> {o.order_group} {o.backorder__order_no}
                </span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

/* ------------ helpers ------------ */

function normalizeData(packingPlan) {
  const plan = Array.isArray(packingPlan?.plan) ? packingPlan.plan : [];
  const blocks = [];

  for (const b of plan) {
    const xIn = toNum(b.x_start) ?? 0; // across width
    const yIn = toNum(b.y_start) ?? 0; // down length
    const widthIn = toNum(b.w) ?? 0;   // across width
    const depthIn = toNum(b.d) ?? 0;   // along length

    blocks.push({
      xIn,
      yIn,
      widthIn,
      depthIn,
      rotated: Boolean(b.rotated),
      tiers: toNum(b.tiers) ?? 1,
      heightIn: toNum(b.height),                    // column stack height from backend
      weightLb: toNum(b.stack_weight_lbs),
      levels: Array.isArray(b.levels) ? b.levels : [], // detailed stack levels
    });
  }

  return blocks;
}

function normalizeOverflow(packingPlan) {
  const raw =
    (Array.isArray(packingPlan?.overflow_blocks) && packingPlan.overflow_blocks) ||
    (Array.isArray(packingPlan?.overflow_columns) && packingPlan.overflow_columns) ||
    [];

  var xStart = 0;
  // Convert overflow “columns” to minimal Block-shape objects for <Block />
  // Position is irrelevant for overflow; we show them inline at a fixed size.

  return raw.map((c, index) => ({
    xIn: xStart,
    yIn: 0,
    widthIn: Number(c.w) || 0,
    depthIn: Number(c.d) || 0,
    rotated: Boolean(c.rotated),
    tiers: Number(c.tiers) || 1,
    heightIn: Number(c.height) || 0,
    weightLb: Number(c.weight) || Number(c.stack_weight_lbs) || 0,
    levels: Array.isArray(c.levels) ? c.levels : [],
  }));
}

function buildTicks(inchesTotal) {
  const step = 12;
  const arr = [];
  for (let v = 0; v <= inchesTotal + 1e-6; v += step) arr.push(+v.toFixed(6));
  return arr;
}

function feetLabel(inches) {
  return `${Math.round(inches / 12)}′`;
}

function inchLabel(inches) {
  return `${Math.round(inches)}″`;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

const isNum = (v) => Number.isFinite(v);
const roundNum = (v) => (isNum(v) ? Math.round(v) : undefined);

// Accept raw number, string, or { parsedValue }
function parsedNumber(v) {
  if (v == null) return undefined;
  if (typeof v === "number") return v;
  if (typeof v === "object" && v && isNum(v.parsedValue)) return v.parsedValue;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function tooltip(b) {
  const lines = [];

  const w = roundNum(b.widthIn);
  const d = roundNum(b.depthIn);
  if (isNum(w) && isNum(d)) lines.push(`Footprint: ${w}" × ${d}"`);

  if (isNum(b.heightIn)) lines.push(`Stack height: ${roundNum(b.heightIn)}"`);
  if (isNum(b.weightLb)) lines.push(`Stack weight: ${roundNum(b.weightLb)} lb`);
  lines.push(`Rotated: ${b.rotated ? "Yes" : "No"}`);
  if ((b.tiers || 0) > 1) lines.push(`Tiers: ${b.tiers}`);

  // Levels / skids (safe)
  if (Array.isArray(b.levels) && b.levels.length) {
    b.levels.forEach((lvl, idx) => {
      lines.push(`Level ${idx + 1}:`);
      const skids = Array.isArray(lvl.skids) ? lvl.skids : [];
      skids.forEach((sk, j) => {
        // order fields can be top-level or nested in details
        const details = sk.details || {};
        const orderNo = sk.order_no ?? details.order_no;
        const orderGrp = sk.order_group ?? details.order_group;
        const backorder = sk.backorder__order_no ?? details.backorder;

        const sw = roundNum(parsedNumber(sk.w));
        const sd = roundNum(parsedNumber(sk.d));
        const sh = roundNum(parsedNumber(sk.h));

        const parts = [];
        parts.push(`  Skid ${j + 1}${orderNo ? ` — Order ${orderNo}` : ""}`);
        if (orderGrp) parts.push(`  ${orderGrp}`);
        if (backorder) parts.push(`  ${backorder}`);
        if (isNum(sw) && isNum(sd) && isNum(sh)) {
          parts.push(`  ${sw}"×${sd}"×${sh}"`);
        }
        lines.push(parts.join(""));
      });
    });
  }

  return lines.join("\n");
}
